import { useMemo, useState } from 'react';
import { useLoaderData } from 'react-router';
import { Calendar, Plus } from 'lucide-react';

import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import type { ProjectTeamMemberView } from '@modules/professional/type/project-team-member';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    DetailScrollReveal,
    SECTION_TITLE_TYPE,
    TeamMemberOverlay,
} from '~/ui-rocco/pages/detail-shared';
import { cn } from '@rocco/utils/cn';
import { button } from '~/ui-rocco/button-system';
import {
    useBookMeetingGateEnabled,
    useOpenBookMeetingGate,
} from '~/modules-rocco/booking/book-meeting-gate-state';

/** What a tile needs to open the booking gate on this person's own practice. */
interface TeamTileBooking {
    professionalUrlSegment: string;
    professionalName: string;
    companyLogo: string;
    logoBackgroundColor: string;
    /** Only a real name — the pane must not open on a placeholder. */
    memberName?: string;
}

interface TeamTile {
    id: number;
    /** The line the tile leads with: the person, or their practice when the record has no name. */
    title: string;
    /** The line under it: the person's role, as the live section and the profile's Who we are both set it. */
    subtitle: string;
    thumbnail: string;
    /** Null when the credit carries no link to a profile to book against. */
    booking: TeamTileBooking | null;
    /** The discipline the project credits them under, for the overlay. */
    category: string;
    /** Whether the title above is the person's own name or their practice. */
    isPerson: boolean;
    /** The record itself, which the overlay reads the rest of the person from. */
    member: ProjectTeamMemberView['TeamMembers'][number];
}

/**
 * The names the API hands back for a credited record that has neither its own
 * name nor a linked member. They are placeholders, not names, so a tile leads
 * with the practice instead of printing them.
 */
const PLACEHOLDER_NAMES = new Set(['Unnamed Team Member', 'Unnamed Member']);

/** The practice's profile slug, from the Link the credit carries. */
const urlSegmentFromLink = (link: string) =>
    link.split('/').filter(Boolean).pop() ?? '';

/**
 * One member, as the profile's Who we are sets a member: portrait, name,
 * practice, with Book a meeting resting on the portrait.
 *
 * The add control is its mirror on the right: square, so the pair reads as one
 * row of controls on the photograph rather than two different button families.
 */
const TeamSlide = ({
    tile,
    onBookMeeting,
    onOpen,
}: {
    tile: TeamTile;
    onBookMeeting?: (tile: TeamTile) => void;
    onOpen: (tile: TeamTile) => void;
}) => (
    <div className="flex flex-col">
        <div className="relative aspect-[437/524] w-full overflow-hidden rounded-tile bg-white/5">
            {tile.thumbnail ? (
                <img
                    src={tile.thumbnail}
                    alt={tile.title}
                    loading="lazy"
                    className="absolute inset-0 size-full object-cover"
                />
            ) : null}
            {onBookMeeting ? (
                <button
                    type="button"
                    onClick={() => onBookMeeting(tile)}
                    // Named here because the label is hidden on a phone.
                    aria-label="Book a meeting"
                    className={button(
                        'lifted',
                        ['m', 'l'],
                        'absolute bottom-4 left-4',
                        { icon: 'max-md' },
                    )}
                >
                    <Calendar
                        aria-hidden="true"
                        className="size-[1.125rem] shrink-0"
                    />
                    <span className="max-md:hidden">Book a meeting</span>
                </button>
            ) : null}
            <button
                type="button"
                onClick={() => onOpen(tile)}
                aria-label={`About ${tile.title}`}
                className={button(
                    'lifted',
                    ['m', 'l'],
                    'absolute right-4 bottom-4',
                    { icon: true },
                )}
            >
                <Plus aria-hidden="true" className="size-[1.125rem] shrink-0" />
            </button>
        </div>
        <p className="m-0 font-sans leading-snug font-medium text-white max-md:pt-3 max-md:text-base md:pt-[0.625rem] md:text-[0.875rem]">
            {tile.title}
        </p>
        {tile.subtitle ? (
            <p className="m-0 font-sans leading-snug text-white/40 max-md:text-sm md:text-[0.875rem]">
                {tile.subtitle}
            </p>
        ) : null}
    </div>
);

/**
 * Project team — the people the project credits, in the profile's Who we are
 * layout: the title, then a full-bleed four-column carousel of portraits.
 *
 * The title is set in the display serif the page's other section heads use,
 * rather than the profile's sans: this page speaks in the editorial voice.
 *
 * The loader groups the members by their credited category and a person can be
 * credited in more than one, so the list is flattened and deduplicated. Hides
 * itself when the project credits nobody.
 */
export const ProjectTeamSection = () => {
    const { project, projectTeamMembers } = useLoaderData<typeof loader>();
    const bookMeetingEnabled = useBookMeetingGateEnabled();
    const openBookMeetingGate = useOpenBookMeetingGate();
    const [openTile, setOpenTile] = useState<TeamTile | null>(null);

    // Built once per payload: the tiles feed a carousel and an overlay that
    // both hold one of them in state, so rebuilding the array on every render
    // hands the slides a new object each time the overlay opens or closes.
    const tiles = useMemo(() => {
        const seen = new Set<number>();
        const built: TeamTile[] = [];
        for (const group of projectTeamMembers ?? []) {
            for (const member of group.TeamMembers ?? []) {
                if (seen.has(member.ID)) continue;
                seen.add(member.ID);
                const named =
                    member.Name && !PLACEHOLDER_NAMES.has(member.Name);
                const professionalUrlSegment = urlSegmentFromLink(
                    member.Link ?? '',
                );
                built.push({
                    id: member.ID,
                    title: named ? member.Name : member.ProfessionalName,
                    subtitle: member.JobTitle,
                    thumbnail: member.Thumbnail,
                    category: group.Category,
                    isPerson: Boolean(named),
                    member,
                    booking: professionalUrlSegment
                        ? {
                              professionalUrlSegment,
                              professionalName: member.ProfessionalName,
                              companyLogo: member.CompanyLogo ?? '',
                              logoBackgroundColor:
                                  member.LogoBackgroundColor ?? '',
                              memberName: named ? member.Name : undefined,
                          }
                        : null,
                });
            }
        }
        return built;
    }, [projectTeamMembers]);

    if (tiles.length === 0) return null;

    // No session ids: the page only holds the bookable sessions of the practice
    // that published the project, and a tile can credit any of the others.
    const onBookMeeting = bookMeetingEnabled
        ? (tile: TeamTile) => {
              if (!tile.booking) return;
              openBookMeetingGate({
                  professionalUrlSegment: tile.booking.professionalUrlSegment,
                  siteTreeId: project.ID,
                  itemTitle: project.Title,
                  preferredTeamMemberId: tile.id,
                  preferredTeamMemberName: tile.booking.memberName,
                  paneImage: tile.thumbnail || undefined,
                  professionalName: tile.booking.professionalName,
                  companyLogo: tile.booking.companyLogo,
                  logoBackgroundColor: tile.booking.logoBackgroundColor,
              });
          }
        : undefined;

    return (
        <section className="overflow-hidden">
            <div className="mx-auto w-full max-w-(--ap-page-width) max-md:px-6 max-md:pt-5 md:px-[3rem] md:pt-[1.875rem]">
                <h2 className={cn('m-0 text-white', SECTION_TITLE_TYPE)}>
                    Project team
                </h2>
            </div>
            <DetailScrollReveal
                delay={120}
                className="w-full max-md:pt-8 md:pt-[3rem]"
            >
                <Carousel
                    ariaLabel="Project team"
                    options={{
                        align: 'start',
                        containScroll: 'trimSnaps',
                        dragFree: true,
                        slidesToScroll: 'auto',
                        breakpoints: {
                            '(min-width: 768px)': { slidesToScroll: 1 },
                        },
                    }}
                >
                    {/* Gutter padding on the viewport keeps the first and last
                        snaps on the page gutter while the slides run full-bleed
                        to the screen's edge, as the profile's rail does. */}
                    <CarouselContent
                        className="max-md:gap-3 md:-mx-[0.375rem]"
                        viewportClassName="max-md:px-4 md:px-[3rem] qhd:px-[calc((100%_-_120rem)/2_+_3rem)]"
                    >
                        {tiles.map((tile) => (
                            <CarouselItem
                                key={tile.id}
                                className="max-md:basis-[46%] md:basis-1/4 md:px-[0.375rem]"
                            >
                                <TeamSlide
                                    tile={tile}
                                    onBookMeeting={
                                        tile.booking ? onBookMeeting : undefined
                                    }
                                    onOpen={setOpenTile}
                                />
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </Carousel>
            </DetailScrollReveal>
            {openTile ? (
                <TeamMemberOverlay
                    displayName={openTile.title}
                    isPerson={openTile.isPerson}
                    jobTitle={openTile.member.JobTitle}
                    summary={openTile.member.Summary}
                    thumbnail={openTile.member.Thumbnail}
                    licenseNumber={openTile.member.LicenseNumber}
                    practice={{
                        id: openTile.member.ProfessionalID,
                        name: openTile.member.ProfessionalName,
                        link: openTile.member.Link,
                        companyLogo: openTile.member.CompanyLogo,
                        logoBackgroundColor:
                            openTile.member.LogoBackgroundColor,
                        customEnquiryMessage:
                            openTile.member.CustomEnquiryMessage,
                        enquiryMessageOverrideEnabled:
                            openTile.member.EnquiryMessageOverrideEnabled,
                        enquiryMessagePlaceholder:
                            openTile.member.EnquiryMessagePlaceholder,
                    }}
                    subject={{
                        itemType: 'project',
                        siteTreeID: project.ID,
                        itemTitle: project.Title,
                        relatedItem: {
                            id: project.ID,
                            type: 'project',
                            title: project.Title,
                            category: project.categoryPath,
                        },
                    }}
                    onBookMeeting={
                        onBookMeeting && openTile.booking
                            ? () => onBookMeeting(openTile)
                            : undefined
                    }
                    onClose={() => setOpenTile(null)}
                />
            ) : null}
        </section>
    );
};
